import { Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  Navigate,
  generatePath,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ModalComponent, EConfirmModalType } from "src/shared/components";
import { ContractBody } from "src/shared/components/contracts/ContractBody";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { EContractState } from "src/shared/types/partner.types";
import {
  useGetpartnerComplexContractsListQuery,
  usePostPartnerVirtualContractMutation,
} from "../../../model/api/api";
import { getVirtualContractDefaultFormValues } from "../../../model/constants/formValues";
import { IPartnerVirtualContractFormValues } from "../../../model/types";
import { ComplexContractDialogFooter } from "../complexContracts/ComplexContractDialogFooter";
import { VirtualContractForm } from "./VirtualContractForm";

export function NewPartnerVirtualContract() {
  const { partner_id } = useParams();
  const navigate = useNavigate();
  const goBackRoute = partner_id
    ? generatePath(routes.partnerContractsList, { partner_id })
    : "/404";

  const [postContract, { isLoading: isLoadingPost }] =
    usePostPartnerVirtualContractMutation();

  const handleSaveContract = async (
    data: IPartnerVirtualContractFormValues,
  ) => {
    try {
      await postContract({
        ...data,
        partner_id: Number(partner_id),
        contract_date: data.contract_date.format("YYYY-MM-DD"),
      }).unwrap();
      navigate(goBackRoute);
    } catch (e) {
      showApiError(e);
    }
  };

  const methods = useForm<IPartnerVirtualContractFormValues>({
    mode: "onChange",
    defaultValues: getVirtualContractDefaultFormValues({
      partner_id: Number(partner_id),
    }),
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(goBackRoute),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  const { data: complexContracts } = useGetpartnerComplexContractsListQuery(
    partner_id
      ? {
          partner_id,
        }
      : skipToken,
  );
  const complexContractsOptions = useMemo(
    () =>
      complexContracts?.data
        .filter(
          ({ contract_state }) => contract_state.value !== EContractState._5,
        )
        .map(({ id, contract_number }) => ({
          name: contract_number,
          value: id,
        })),
    [complexContracts],
  );

  if (!partner_id) {
    return <Navigate to="/404" replace />;
  }

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      boxProps={{ sx: { height: "90vh" } }}
      disableBodyPadding
      initialBodyShadow
      title="Новый договор"
      onClose={isDirty ? showModal : () => navigate(goBackRoute)}
      open
      modalFooter={
        <ComplexContractDialogFooter
          isLoading={isLoadingPost}
          onSubmit={handleSubmit(handleSaveContract)}
          onCancel={isDirty ? showModal : () => navigate(goBackRoute)}
        />
      }
    >
      {modal}
      <FormProvider {...methods}>
        <ContractBody
          text="Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
          Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
          Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
          "
          form={
            <VirtualContractForm
              partnerId={partner_id}
              complexContractsOptions={complexContractsOptions || []}
            />
          }
        />
      </FormProvider>
    </ModalComponent>
  );
}
